"use client";

import { useModal } from "@/contexts/ModalContext";
import { createContext, useCallback, useContext, useReducer } from "react";

const FlightSelectionContext = createContext();

// Initial state
const initialState = {
  selectedFlight: null,
  selectedPackage: null,
  loading: false,
  error: null,
  // Yeni: Sıralı paket seçimi için
  flightSequence: [],
  currentSequenceIndex: 0,
  selectedPackages: [], // Tüm seçilen paketler
  isSequentialSelection: false,
  parentCombinedFlight: null, // Combined flight bilgisi (ID ve ReferenceId için)
};

// Action types
const ACTIONS = {
  SELECT_FLIGHT: "SELECT_FLIGHT",
  SELECT_PACKAGE: "SELECT_PACKAGE",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  RESET: "RESET",
  // Yeni: Sıralı paket seçimi için
  START_SEQUENTIAL_SELECTION: "START_SEQUENTIAL_SELECTION",
  NEXT_SEQUENCE_FLIGHT: "NEXT_SEQUENCE_FLIGHT",
  ADD_SELECTED_PACKAGE: "ADD_SELECTED_PACKAGE",
  COMPLETE_SEQUENCE: "COMPLETE_SEQUENCE",
  SET_PARENT_COMBINED_FLIGHT: "SET_PARENT_COMBINED_FLIGHT",
};

// Reducer
const flightSelectionReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SELECT_FLIGHT:
      return {
        ...state,
        selectedFlight: action.payload,
        selectedPackage: null, // Reset package when flight changes
      };
    case ACTIONS.SELECT_PACKAGE:
      return {
        ...state,
        selectedPackage: action.payload,
      };
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case ACTIONS.RESET:
      return initialState;
    // Yeni: Sıralı paket seçimi reducer'ları
    case ACTIONS.START_SEQUENTIAL_SELECTION:
      return {
        ...state,
        flightSequence: action.payload.flightSequence,
        currentSequenceIndex: 0,
        selectedPackages: [],
        isSequentialSelection: true,
        selectedFlight: action.payload.flightSequence[0] || null,
        parentCombinedFlight: action.payload.parentCombinedFlight || null,
      };
    case ACTIONS.NEXT_SEQUENCE_FLIGHT:
      const nextIndex = state.currentSequenceIndex + 1;
      return {
        ...state,
        currentSequenceIndex: nextIndex,
        selectedFlight: state.flightSequence[nextIndex] || null,
      };
    case ACTIONS.ADD_SELECTED_PACKAGE:
      return {
        ...state,
        selectedPackages: [...state.selectedPackages, action.payload],
      };
    case ACTIONS.COMPLETE_SEQUENCE:
      return {
        ...state,
        isSequentialSelection: false,
        flightSequence: [],
        currentSequenceIndex: 0,
        parentCombinedFlight: null,
      };
    case ACTIONS.SET_PARENT_COMBINED_FLIGHT:
      return {
        ...state,
        parentCombinedFlight: action.payload,
      };
    default:
      return state;
  }
};

// Provider component
export const FlightSelectionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(flightSelectionReducer, initialState);

  // Modal context integration
  const packageSelectModal = useModal("package-select-modal");

  // Actions
  const openModal = useCallback(
    (flight) => {
      if (!flight) {
        console.warn(
          "FlightSelectionContext.openModal: Flight objesi bulunamadı",
        );
        return;
      }

      // Reservation sayfası kontrolü: __onPackageSelect veya __legIndex varsa reservation sayfasından geliyor
      const isReservationContext = !!(
        flight?.__onPackageSelect || flight?.__legIndex !== undefined
      );

      // Paket kontrolü: Reservation sayfasında FlightPackageDetails uzunluğuna, diğer yerlerde IsPackageExist'e bak
      let hasPackages = false;

      if (isReservationContext) {
        // Reservation sayfası: FlightPackageDetails array uzunluğuna bak
        const packageDetails =
          flight?.PackageDetails || flight?.FlightPackageDetails || [];
        hasPackages = packageDetails.length > 0;

        if (!hasPackages) {
          console.warn(
            "FlightSelectionContext.openModal: Reservation sayfasında FlightPackageDetails bulunamadı",
            {
              flightId: flight?.Id || flight?.id,
              packageDetailsLength: packageDetails.length,
              hasPackageDetails: !!flight?.PackageDetails,
              hasFlightPackageDetails: !!flight?.FlightPackageDetails,
            },
          );
          return;
        }
      } else {
        // Listing sayfası: IsPackageExist VE PackageDetails uzunluğu kontrolü
        // Combined flight kontrolü (LegOptionsList yapısı)
        if (flight.combinedFlight || flight.combinedFlights) {
          const combinedFlights =
            flight.combinedFlight || flight.combinedFlights;

          // Her leg için hem IsPackageExist hem de PackageDetails uzunluğu kontrolü yap
          const departureFlight =
            combinedFlights?.departure || combinedFlights?.departureFlight;
          const returnFlight =
            combinedFlights?.return || combinedFlights?.returnFlight;
          const otherFlight = combinedFlights?.other;

          const departureIsPackageExist =
            departureFlight?.IsPackageExist === true;
          const departureHasPackages =
            (departureFlight?.PackageDetails || []).length > 0;

          const returnIsPackageExist = returnFlight?.IsPackageExist === true;
          const returnHasPackages =
            (returnFlight?.PackageDetails || []).length > 0;

          const otherIsPackageExist = otherFlight?.IsPackageExist === true;
          const otherHasPackages =
            (otherFlight?.PackageDetails || []).length > 0;

          // En az bir leg'de hem IsPackageExist true hem de PackageDetails dolu olmalı
          hasPackages =
            (departureIsPackageExist && departureHasPackages) ||
            (returnIsPackageExist && returnHasPackages) ||
            (otherIsPackageExist && otherHasPackages);

          if (!hasPackages) {
            console.warn(
              "FlightSelectionContext.openModal: Combined flight'ta hiçbir leg'de paket bulunamadı",
              {
                departureIsPackageExist,
                departureHasPackages,
                departurePackageDetailsLength: (
                  departureFlight?.PackageDetails || []
                ).length,
                returnIsPackageExist,
                returnHasPackages,
                returnPackageDetailsLength: (returnFlight?.PackageDetails || [])
                  .length,
                otherIsPackageExist,
                otherHasPackages,
                otherPackageDetailsLength: (otherFlight?.PackageDetails || [])
                  .length,
              },
            );
            return;
          }
        } else {
          // Tek flight durumu: Hem IsPackageExist hem de PackageDetails uzunluğu kontrolü
          const isPackageExist = flight?.IsPackageExist === true;
          const packageDetails = flight?.PackageDetails || [];
          const hasPackageDetails = packageDetails.length > 0;

       

          hasPackages = isPackageExist && hasPackageDetails;

          if (!hasPackages) {
            console.warn(
              "❌ FlightSelectionContext.openModal: Flight'ta paket bulunamadı - Modal açılmıyor",
              {
                flightId: flight?.Id || flight?.id,
                isPackageExist: flight?.IsPackageExist,
                isPackageExistType: typeof flight?.IsPackageExist,
                packageDetailsLength: packageDetails.length,
                hasPackageDetails,
                packageDetails: packageDetails,
              },
            );
            return;
          }

        }
      }

      dispatch({ type: ACTIONS.SELECT_FLIGHT, payload: flight });
      packageSelectModal.open();
    },
    [packageSelectModal],
  );

  const closeModal = useCallback(() => {
    packageSelectModal.close();
    dispatch({ type: ACTIONS.RESET });
  }, [packageSelectModal]);

  const selectFlight = useCallback((flight) => {
    dispatch({ type: ACTIONS.SELECT_FLIGHT, payload: flight });
  }, []);

  const selectPackage = useCallback((packageDetails) => {
    dispatch({ type: ACTIONS.SELECT_PACKAGE, payload: packageDetails });
  }, []);

  const setLoading = useCallback((loading) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: ACTIONS.SET_ERROR, payload: error });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: ACTIONS.RESET });
  }, []);

  // Yeni: Sıralı paket seçimi fonksiyonları
  const startSequentialSelection = useCallback(
    (flightSequence, parentCombinedFlight = null) => {
      dispatch({
        type: ACTIONS.START_SEQUENTIAL_SELECTION,
        payload: { flightSequence, parentCombinedFlight },
      });
      packageSelectModal.open();
    },
    [packageSelectModal],
  );

  const nextSequenceFlight = useCallback(() => {
    dispatch({ type: ACTIONS.NEXT_SEQUENCE_FLIGHT });
  }, []);

  const addSelectedPackage = useCallback((packageData) => {
    dispatch({
      type: ACTIONS.ADD_SELECTED_PACKAGE,
      payload: packageData,
    });
  }, []);

  const completeSequence = useCallback(() => {
    dispatch({ type: ACTIONS.COMPLETE_SEQUENCE });
    packageSelectModal.close();
  }, [packageSelectModal]);

  // Sıralı paket seçimi işleme fonksiyonu
  const processSequentialPackageSelection = useCallback(
    (flightSequence, parentCombinedFlight = null) => {
      // Listing ile aynı kural (openModal): sadece PackageDetails uzunluğu yetmez —
      // RT ayrı listelerde gidiş IsPackageExist: false iken API yine boş/placeholder
      // PackageDetails döndürebiliyor. Bu durumda gidişi "paketli" sanıp sıraya koymak
      // dönüş seçiminde yeniden gidiş paketi modalını açtırıyordu.
      const validFlights = flightSequence.filter(
        (flight) =>
          flight &&
          flight.IsPackageExist === true &&
          (flight.PackageDetails || []).length > 0,
      );

      if (validFlights.length === 0) {
        // Hiçbir flight'ta paket yok → direkt reservation
        return { shouldProceedToReservation: true };
      }

      // Sıralı paket seçimi başlat (combined flight bilgisini de geçir)
      startSequentialSelection(validFlights, parentCombinedFlight);
      return { shouldProceedToReservation: false };
    },
    [startSequentialSelection],
  );

  // Computed values
  const hasSelectedFlight = !!state.selectedFlight;
  const hasSelectedPackage = !!state.selectedPackage;
  const canProceed = hasSelectedFlight && hasSelectedPackage;
  const isModalOpen = packageSelectModal.isOpen;

  // Yeni computed values
  const isSequentialSelection = state.isSequentialSelection;
  const currentSequenceIndex = state.currentSequenceIndex;
  const flightSequence = state.flightSequence;
  const selectedPackages = state.selectedPackages;
  const parentCombinedFlight = state.parentCombinedFlight;
  const isLastFlightInSequence =
    currentSequenceIndex >= flightSequence.length - 1;

  const value = {
    // State
    ...state,
    isModalOpen,

    // Actions
    openModal,
    closeModal,
    selectFlight,
    selectPackage,
    setLoading,
    setError,
    reset,

    // Yeni: Sıralı paket seçimi actions
    startSequentialSelection,
    nextSequenceFlight,
    addSelectedPackage,
    completeSequence,
    processSequentialPackageSelection,

    // Computed values
    hasSelectedFlight,
    hasSelectedPackage,
    canProceed,

    // Yeni computed values
    isSequentialSelection,
    currentSequenceIndex,
    flightSequence,
    selectedPackages,
    parentCombinedFlight,
    isLastFlightInSequence,
  };

  return (
    <FlightSelectionContext.Provider value={value}>
      {children}
    </FlightSelectionContext.Provider>
  );
};

// Hook
export const useFlightSelection = () => {
  const context = useContext(FlightSelectionContext);
  if (!context) {
    throw new Error(
      "useFlightSelection must be used within FlightSelectionProvider",
    );
  }
  return context;
};
